import {
  BooleanField,
  CreateButton,
  Datagrid,
  FunctionField,
  List,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
  usePermissions,
} from "react-admin";
import DisplayDateField from "@/components/DisplayDateField";
import type { RaRecord } from "react-admin";
import type { SxProps } from "@mui/material";
import { Box, Chip, Typography, useMediaQuery } from "@mui/material";

import { formatDisplayDateTime } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

import { RelayEventLiveToggle } from "./RelayEventLiveToggle";

const listRowSx = (): SxProps => ({
  borderBottom: 1,
  borderBottomColor: "divider",
});

const formatEventTypeLabel = (record: RaRecord) => {
  if (record.isDefault) return "Default";
  if (record.requiresAccessGrant) return "Gated";
  return null;
};

const listFilters = [<TextInput key="q" source="q" label="Search" alwaysOn resettable />];

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

const RelayEventsList = () => {
  const { permissions } = usePermissions();
  const canEdit = hasPermission(permissions, "relayEvents.edit");
  const isNarrow = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  return (
    <List
      sort={{ field: "createdAt", order: "DESC" }}
      actions={<ListActions />}
      filters={listFilters}
    >
      {isNarrow ? (
        <SimpleList
          rowClick={canEdit ? "edit" : false}
          primaryText={(record: RaRecord) => (
            <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>
              {String(record.name ?? "—")}
            </Typography>
          )}
          secondaryText={(record: RaRecord) => {
            const typeLabel = formatEventTypeLabel(record);
            const parts = [
              record.title ? String(record.title) : null,
              typeLabel,
              record.isActive ? "Active" : "Inactive",
              record.otpRequired ? "OTP required" : null,
              record.eventTime
                ? `Starts ${formatDisplayDateTime(record.eventTime, { empty: "—" })}`
                : null,
            ].filter(Boolean);
            return parts.join(" · ") || "—";
          }}
          rightIcon={(record: RaRecord) => (
            <RelayEventLiveToggle record={record} canEdit={canEdit} />
          )}
          rowSx={() => listRowSx()}
        />
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Datagrid
            rowClick={canEdit ? "edit" : false}
            bulkActionButtons={false}
            sx={{ minWidth: 880 }}
          >
            <TextField source="name" label="Name" />
            <TextField source="title" label="Title" emptyText="—" />
            <FunctionField
              label="Type"
              render={(record: RaRecord) => {
                if (record.isDefault) {
                  return <Chip label="Default" size="small" color="primary" />;
                }
                if (record.requiresAccessGrant) {
                  return <Chip label="Gated" size="small" color="secondary" />;
                }
                return "—";
              }}
            />
            <BooleanField source="isActive" label="Active" />
            <FunctionField
              label="Live"
              sortBy="isLive"
              render={(record: RaRecord) => (
                <RelayEventLiveToggle record={record} canEdit={canEdit} />
              )}
            />
            <BooleanField source="otpRequired" label="OTP" />
            <DisplayDateField source="eventTime" label="Starts" showTime emptyText="—" />
          </Datagrid>
        </Box>
      )}
    </List>
  );
};

export default RelayEventsList;
